import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const FilterPanel = ({ filters, onFilterChange, onApply, loading }) => {
  // Initialize local filters with proper dayjs conversion
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || '',
    damageType: filters.damageType || '',
    startDate: filters.startDate ? (typeof filters.startDate === 'string' ? dayjs(filters.startDate) : filters.startDate) : null,
    endDate: filters.endDate ? (typeof filters.endDate === 'string' ? dayjs(filters.endDate) : filters.endDate) : null,
    district: filters.district || ''
  });

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters({
      status: filters.status || '',
      damageType: filters.damageType || '',
      startDate: filters.startDate ? (typeof filters.startDate === 'string' ? dayjs(filters.startDate) : filters.startDate) : null,
      endDate: filters.endDate ? (typeof filters.endDate === 'string' ? dayjs(filters.endDate) : filters.endDate) : null,
      district: filters.district || ''
    });
  }, [filters]);
  
  // Status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'processing', label: 'Processing' },
    { value: 'approved', label: 'Approved' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' }
  ];
  
  // Damage type options
  const damageTypeOptions = [
    { value: '', label: 'All Damage Types' },
    { value: 'house', label: 'House Damage' },
    { value: 'crop', label: 'Crop Damage' },
    { value: 'livestock', label: 'Livestock Loss' },
    { value: 'land', label: 'Land Damage' },
  ];
  
  // Handle filter change
  const handleChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
  };
  
  // Apply filters
  const handleApply = () => {
    // Convert dates to string format for API
    const apiFilters = {
      ...localFilters,
      startDate: localFilters.startDate ? dayjs(localFilters.startDate).format('YYYY-MM-DD') : null,
      endDate: localFilters.endDate ? dayjs(localFilters.endDate).format('YYYY-MM-DD') : null
    };
    onFilterChange(apiFilters);
    onApply();
  };
  
  // Clear all filters
  const handleClear = () => {
    const clearedFilters = {
      status: '',
      damageType: '',
      startDate: null,
      endDate: null,
      district: ''
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
    onApply();
  };
  
  // Check if any filter is active
  const hasActiveFilters = () => {
    return localFilters.status || localFilters.damageType || 
           localFilters.startDate || localFilters.endDate || localFilters.district;
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Filter geospatial data by various criteria
        </Typography>
        
        {/* Active Filters Display */}
        {hasActiveFilters() && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              Active Filters:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {localFilters.status && (
                <Chip
                  label={`Status: ${localFilters.status}`}
                  size="small"
                  onDelete={() => handleChange('status', '')}
                  color="primary"
                />
              )}
              {localFilters.damageType && (
                <Chip
                  label={`Type: ${localFilters.damageType}`}
                  size="small"
                  onDelete={() => handleChange('damageType', '')}
                  color="primary"
                />
              )}
              {localFilters.startDate && (
                <Chip
                  label={`From: ${dayjs(localFilters.startDate).format('DD/MM/YYYY')}`}
                  size="small"
                  onDelete={() => handleChange('startDate', null)}
                  color="primary"
                />
              )}
              {localFilters.endDate && (
                <Chip
                  label={`To: ${dayjs(localFilters.endDate).format('DD/MM/YYYY')}`}
                  size="small"
                  onDelete={() => handleChange('endDate', null)}
                  color="primary"
                />
              )}
            </Stack>
          </Box>
        )}
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Status Filter */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Application Status</InputLabel>
          <Select
            value={localFilters.status}
            label="Application Status"
            onChange={(e) => handleChange('status', e.target.value)}
          >
            {statusOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Damage Type Filter */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Damage Type</InputLabel>
          <Select
            value={localFilters.damageType}
            label="Damage Type"
            onChange={(e) => handleChange('damageType', e.target.value)}
          >
            {damageTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Date Range Filters */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1 }}>
          Incident Date Range
        </Typography>
        
        <DatePicker
          label="Start Date"
          value={localFilters.startDate}
          onChange={(newValue) => handleChange('startDate', newValue)}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { mb: 2 }
            }
          }}
          maxDate={localFilters.endDate ? dayjs(localFilters.endDate) : dayjs()}
        />
        
        <DatePicker
          label="End Date"
          value={localFilters.endDate}
          onChange={(newValue) => handleChange('endDate', newValue)}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { mb: 2 }
            }
          }}
          minDate={localFilters.startDate ? dayjs(localFilters.startDate) : undefined}
          maxDate={dayjs()}
        />
        
        {/* District Filter */}
        <TextField
          fullWidth
          label="District"
          value={localFilters.district}
          onChange={(e) => handleChange('district', e.target.value)}
          placeholder="Enter district name"
          sx={{ mb: 3 }}
        />
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Action Buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            onClick={handleApply}
            disabled={loading}
          >
            {loading ? 'Applying...' : 'Apply Filters'}
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            disabled={loading || !hasActiveFilters()}
          >
            Clear All
          </Button>
        </Stack>
        
        {/* Filter Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">
            <strong>Tip:</strong> Use filters to narrow down locations and improve map performance. 
            Date filters are based on the natural calamity occurrence date.
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default FilterPanel;
